/**
 * 전투 연출용 단명 FX 오브젝트 풀 3종 — Image·Text·Rectangle.
 * 매 발사·충돌마다 생성/소멸하던 Phaser 오브젝트를 재사용해 GC churn을 제거한다.
 * 각 팩토리가 반환하는 ObjectPool은 `BattlefieldRunner` / `ModuleGridView`에서 lazy 생성된다.
 *
 * | 팩토리              | 오브젝트 종류        | 주요 사용처              |
 * |---------------------|----------------------|--------------------------|
 * | createFxImagePool   | Image                | 잔상·파편·파괴 조각      |
 * | createFxTextPool    | Text                 | 데미지·크리티컬 팝업     |
 * | createFxRectPool    | Rectangle            | 발동 펄스(테두리 확대)   |
 *
 * ⚠ **공통 주의**: acquire한 오브젝트는 직전 사용 상태(텍스처·blend·scale·alpha 등)가 남아 있으므로
 * 호출자가 emit마다 모든 시각 속성을 명시 재설정해야 한다. onRelease가 tween kill + 비활성화로 idle
 * 상태를 정리하지만, 타입별로 추가 리셋(blend, scale 등)이 각 팩토리 주석에 명시돼 있다.
 */
import Phaser from "phaser";
import { ObjectPool } from "@yjsgames/object-pool";

/**
 * 단명 FX Image 풀 — 잔상 고스트·피격 파편·파괴 조각 공용(전부 scene.add.image + tween + destroy 패턴).
 * 발사체처럼 매 프레임/매 충돌마다 생성·소멸하던 Image를 재사용해 GC churn을 제거한다.
 *
 * ⚠ acquire한 Image는 직전 사용의 상태(텍스처·blend·rotation·scale·alpha·mask)가 남아 있으므로,
 * 호출자가 emit마다 **모든 시각 속성을 명시 재설정**해야 한다(특히 blendMode·rotation — 용도별로 다름).
 * onRelease가 tween kill + 마스크 해제 + 비활성화로 idle 상태를 정리한다.
 */
export type FxImagePool = ObjectPool<Phaser.GameObjects.Image>;

/**
 * FX 이미지 풀 생성 — 비활성 Image를 만들고 재사용한다.
 * @param initialTexture 신규 인스턴스의 초기 텍스처(존재하는 키). 재사용 시 호출자가 setTexture로 교체한다.
 */
export function createFxImagePool(scene: Phaser.Scene, initialTexture: string): FxImagePool {
  return new ObjectPool<Phaser.GameObjects.Image>({
    create: () => scene.add.image(0, 0, initialTexture).setActive(false).setVisible(false),
    onRelease: (img) => {
      scene.tweens.killTweensOf(img);
      img.clearMask();
      // blend를 NORMAL로 복원 — 잔상(ADD) 후 파편/조각(NORMAL)으로 재사용 시 stale ADD 누출 방지.
      // 잔상 emit은 acquire 후 다시 ADD로 set한다. (rotation/scale/alpha/texture는 각 emit이 재설정.)
      img.setBlendMode(Phaser.BlendModes.NORMAL);
      img.setActive(false).setVisible(false);
    },
  });
}

/** 단명 FX Text 풀 — 데미지·크리티컬 팝업 등 매 충돌마다 뜨는 고빈도 텍스트 재사용. */
export type FxTextPool = ObjectPool<Phaser.GameObjects.Text>;

/**
 * FX 텍스트 풀 생성 — 비활성 Text를 만들고 재사용한다. floatingText(phaser-fx)의 `pool` 옵션에 주입.
 * 재사용 시 floatingText가 setText/setStyle/위치/alpha를 전부 재설정한다.
 */
export function createFxTextPool(scene: Phaser.Scene): FxTextPool {
  return new ObjectPool<Phaser.GameObjects.Text>({
    create: () => scene.add.text(0, 0, "", {}).setActive(false).setVisible(false),
    onRelease: (txt) => {
      scene.tweens.killTweensOf(txt);
      txt.clearMask();
      txt.setActive(false).setVisible(false);
    },
  });
}

/** 단명 FX Rectangle 풀 — 발동 펄스(테두리 확대 페이드) 등 매 발사 뜨는 고빈도 사각 오버레이 재사용. */
export type FxRectPool = ObjectPool<Phaser.GameObjects.Rectangle>;

/**
 * FX 사각 풀 생성 — 비활성 Rectangle을 만들고 재사용한다(펄스 연출 등). 발동 펄스는 scale 1→1.22 tween을
 * 쓰므로 ⚠ onRelease에서 scale을 1로 복원해야 다음 acquire가 stale scale을 이어받지 않는다.
 * 재사용 시 호출자가 위치·크기·fill/stroke·alpha·depth를 전부 재설정한다.
 */
export function createFxRectPool(scene: Phaser.Scene): FxRectPool {
  return new ObjectPool<Phaser.GameObjects.Rectangle>({
    create: () => scene.add.rectangle(0, 0, 0, 0).setActive(false).setVisible(false),
    onRelease: (rect) => {
      scene.tweens.killTweensOf(rect);
      rect.clearMask();
      rect.setScale(1);
      rect.setActive(false).setVisible(false);
    },
  });
}
